import { trackError } from '@local/metrics';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext } from 'react';
import { Navigate } from 'react-router';

import {
    useLoadLicenseOrgsQuery,
    useLoadLicenseTokenQuery,
} from '../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import { LOGIN_ERROR_ROUTE } from '../../constants';
import {
    getAvailableEvoInstances,
    getLastUsedInstance,
    setCurrentEvoInstance,
} from '../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { Background } from '../background/Background';
import { LoginContext } from '../loginHandler/LoginContext';

/**
 * This is a temporary component to handle the case where blocksync and storage
 * a) Want to allow users without license tokens.
 * b) Don't want to display the hub selector.
 * It is possible that once b) is no longer the case, we might want to fold this component back in
 * with <CentralOrgSelector />
 */
export function SkipOrgSelector({ homePagePath = '/' }: { homePagePath?: string }) {
    const { service } = useContext(LoginContext);
    const { isLoading, error } = useLoadLicenseOrgsQuery(
        service !== 'none' ? { service } : skipToken,
    );
    const availableEvoInstances = getAvailableEvoInstances();
    const haveLicence = !isLoading && !error && availableEvoInstances.length > 0;
    if (haveLicence) {
        // Use the lastUsedInstance cookie or the first available hub.
        setCurrentEvoInstance(getLastUsedInstance() ?? availableEvoInstances[0]);
    }
    // Have to call this after setting the evo instance, as it uses the hub and org from there.
    const token = useLoadLicenseTokenQuery(haveLicence ? { service } : skipToken);
    const err = error || token.error;
    if (err) {
        trackError('Token fetch failed', 'Error fetching license token in <SkipOrgSelector>', {
            discoverError: error,
            tokenError: token.error,
        });
        // New style apps to handle missing licence tokens on their own rather than kicking them to the error page.
        // Errors still take us back to the login page though.
        return <Navigate to={`${LOGIN_ERROR_ROUTE}?error=entitlements_failure`} />;
    }

    if (isLoading || token.isLoading) {
        return (
            <Background>
                <Grid
                    item
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100px',
                    }}
                    alignContent="center"
                    justifyContent="center"
                    display="grid"
                >
                    <CircularProgress automation-id="spinner-loading" />
                </Grid>
            </Background>
        );
    }
    if (!haveLicence) {
        setCurrentEvoInstance({ hasNoLicense: true });
    }
    return <Navigate to={homePagePath} />;
}
